// src/services/supabaseClient.ts

import { createClient } from "@supabase/supabase-js";
import { Database, Json } from "../types/supabase";

// Get environment variables for Supabase connection
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if environment variables are defined
if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    "Supabase URL or Anonymous Key is not defined in environment variables. Make sure to define VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY."
  );
}

const SUPABASE_STORAGE_PREFIX = "sb-";
export const REMEMBER_ME_STORAGE_KEY = "auth.rememberMe";

const storageHasSupabaseSession = (storage: Storage) => {
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && key.startsWith(SUPABASE_STORAGE_PREFIX)) {
      return true;
    }
  }

  return false;
};

const getInitialRememberMePreference = () => {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    const storedPreference = window.localStorage.getItem(REMEMBER_ME_STORAGE_KEY);

    if (storedPreference === "false") {
      return false;
    }

    if (storedPreference === "true") {
      return true;
    }

    const hasSessionOnlyInSessionStorage = storageHasSupabaseSession(window.sessionStorage);
    const hasPersistentSession = storageHasSupabaseSession(window.localStorage);

    if (hasSessionOnlyInSessionStorage && !hasPersistentSession) {
      return false;
    }
  } catch (error) {
    console.warn("Unable to read remember me preference:", error);
  }

  return true;
};

// Function to initialize Supabase with custom storage
export const initializeSupabase = (rememberMe: boolean) => {
  const isBrowser = typeof window !== "undefined";
  const authStorage = isBrowser
    ? rememberMe
      ? window.localStorage
      : window.sessionStorage
    : undefined;

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      ...(authStorage ? { storage: authStorage } : {}),
      autoRefreshToken: rememberMe,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });
};

export const clearSupabaseAuthStorage = (storage?: Storage) => {
  if (!storage) {
    return;
  }

  const keysToRemove: string[] = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && key.startsWith(SUPABASE_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => storage.removeItem(key));
};

const initialRememberMe = getInitialRememberMePreference();

let supabase = initializeSupabase(initialRememberMe);

// Helper function to check if an error is a session expired error
export const isSessionExpiredError = (error: any): boolean => {
  if (!error) return false;

  const errorMessage = error?.message || error?.error_description || '';
  const errorCode = error?.code || '';

  return (
    errorCode === 'session_expired' ||
    errorMessage.includes('session_expired') ||
    errorMessage.includes('Session Expired') ||
    errorMessage.includes('Invalid Refresh Token') ||
    errorMessage.includes('Refresh Token Not Found') ||
    errorCode === 'refresh_token_not_found'
  );
};

// Helper function to handle session expiration and redirect to login
export const handleSessionExpired = () => {
  if (typeof window !== "undefined") {
    // Clear all Supabase auth storage
    clearSupabaseAuthStorage(window.localStorage);
    clearSupabaseAuthStorage(window.sessionStorage);

    // Redirect to login page if not already there
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }
};

// Helper function to check API response for session errors
export const checkSessionError = (error: any): void => {
  if (isSessionExpiredError(error)) {
    handleSessionExpired();
  }
};

// Set up global auth state listener to handle session expiration
if (typeof window !== "undefined") {
  supabase.auth.onAuthStateChange((event, session) => {
    // Handle token refresh failure or signed out events
    if (event === 'TOKEN_REFRESHED' && !session) {
      // Token refresh failed - session expired
      handleSessionExpired();
    } else if (event === 'SIGNED_OUT') {
      // Clear storage on sign out
      clearSupabaseAuthStorage(window.localStorage);
      clearSupabaseAuthStorage(window.sessionStorage);
    }
  });

  // Also listen for Supabase auth errors globally
  // This catches the "session_expired" error when refresh token is invalid
  const originalGetSession = supabase.auth.getSession.bind(supabase.auth);
  supabase.auth.getSession = async () => {
    try {
      const result = await originalGetSession();
      // Check if there's an error with session_expired code
      if (result.error && isSessionExpiredError(result.error)) {
        handleSessionExpired();
      }
      return result;
    } catch (error: any) {
      // Handle session expired errors thrown as exceptions
      if (isSessionExpiredError(error)) {
        handleSessionExpired();
      }
      throw error;
    }
  };

  // Also wrap refreshSession to catch refresh token errors
  const originalRefreshSession = supabase.auth.refreshSession.bind(supabase.auth);
  supabase.auth.refreshSession = async () => {
    try {
      const result = await originalRefreshSession();
      if (result.error && isSessionExpiredError(result.error)) {
        handleSessionExpired();
      }
      return result;
    } catch (error: any) {
      if (isSessionExpiredError(error)) {
        handleSessionExpired();
      }
      throw error;
    }
  };
}

export { supabase };

export const configureSupabaseClient = (rememberMe: boolean) => {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(REMEMBER_ME_STORAGE_KEY, rememberMe ? "true" : "false");

      const storageToClear = rememberMe ? window.sessionStorage : window.localStorage;
      clearSupabaseAuthStorage(storageToClear);
    } catch (error) {
      console.warn("Unable to persist remember me preference:", error);
    }
  }

  supabase = initializeSupabase(rememberMe);
  return supabase;
};

// Export types for the tables as shown in your schema diagram
export type Organization = Database["public"]["Tables"]["organizations"]["Row"];
export type Branch = Database["public"]["Tables"]["branches"]["Row"];
export type MenuItem = Database["public"]["Tables"]["items"]["Row"];
export type Category = Database["public"]["Tables"]["categories"]["Row"];
export type AgentConfiguration =
  Database["public"]["Tables"]["agent_configuration"]["Row"];
export type Feature = Database["public"]["Tables"]["features"]["Row"];

// Variant System Types
export interface BranchActionConfig {
  id: string;
  data: any;
  branch_id: string;
  form_name: string;
  action_type: string;
  is_required: boolean;
  max_selection: number;
  min_selection: number;
  is_multiselection: boolean;
  ar: Json;
}

export interface ItemVariant {
  id: string;
  data: any;
  item_id: string;
  form_name: string;
  action_type: string;
  branch_action_config_id: string;
  override_data?: any | null;
}

export interface InsertItemVariantPayload {
  item_id: string;
  branch_action_config_id: string;
  override_data?: any;
}

export interface UpdateItemVariantPayload {
  id: string;
  item_id: string;
  branch_action_config_id: string;
  override_data: any;
}

export interface ActionType {
  ar: string;
  name: string;
  description: string;
}

export interface feedbackPaylaod {
  session_id?: string | null;
  is_positive?: boolean | null;
  limit?: number;
  offset?: number;
  item_id?: string | null;
  branch_id?: string | null;
}

export interface Feedback {
  id: string;
  session_id?: string | null;
  is_positive?: boolean | null;
  item_id?: string | null;
  feedback?: string | null;
  item_name?: string | null;
  created_at?: string | null;
  image_url?: string | null;
  normalized_rating?: number | null;
  // extend with any other fields returned by RPC
}

export interface FeedbackSummaryPayload {
  branch_id: string;
  item_filter: 'no_items' | 'all_items' | 'item_id';
  item_id?: string | null;
  written_by: 1 | -1; // 1 for customer, -1 for AI
  attribute_filter: {
    session_id?: string | null;
    is_positive?: boolean | null;
  };
}

export interface FeedbackItem {
  id: string;
  feedback: string;
  created_at: string;
  session_id: string;
  is_positive: boolean | null;
  normalized_rating: number | null;
}

export interface BranchSummary {
  feedbacks: FeedbackItem[];
  avg_rating: number | null;
  total_rated_feedbacks: number | null;
}

export interface ItemSummary {
  name: string;
  item_id: string;
  feedbacks: FeedbackItem[];
  image_url: string | null;
  avg_rating: number | null;
  total_rated_feedbacks: number | null;
}

export interface FeedbackSummaryResponse {
  success: boolean;
  message: string;
  data: {
    branch_summary: BranchSummary | null;
    items_summary: ItemSummary[] | null;
  } | null;
}

export interface itemSearchPayload {
  branch_id: string,
  category_id: string,
  limit: number,
  offset: number
  search: string,
  is_available?: boolean | null,
  is_featured?: boolean | null,
  is_published?: boolean | null,
}

export interface ClientZone {
  zone_name: string;
  order_count: number;
  ar_zone_name: string;
  last_ordered_at: string;
}

export interface ClientInfo {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  last_order_at: string;
  number_of_orders: number;
  total_order_amount: number;
  average_order_amount: number;
  zones?: ClientZone[];
}

export interface GetClientsPayload {
  branch_id: string;
  offset?: number;
  limit?: number;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  min_orders?: number;
  max_orders?: number;
  min_total?: number;
  max_total?: number;
}

export interface GetClientsResponse {
  success: boolean;
  message: string;
  data: ClientInfo[];
  total_count?: number;
}
// Helper functions for common operations

/**
 * Fetches organizations owned by the current user
 */
export const getUserOrganization = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("organizations")
      .select("*")
      .eq("owner_id", userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching branch:", error);
    return null;
  }
};
// export const getUserOrganization = async (userId: string) => {
// 	try {
// 		const data = await supabase.rpc(
// 			'get_organizations_controller',
// 			{
// 				p_setup_status: true
// 			}
// 		)
// 		console.log("get_organizations_controller", data);

// 		// if (error) throw error;
// 		return data;
// 	} catch (error) {
// 		console.error('Error fetching branch:', error);
// 		return null;
// 	}
// };

/**
 * Fetches a branch by ID
 */
export const getBranchById = async (
  branchId: string,
  organizationId: string
): Promise<Branch | null> => {
  try {
    const { data, error } = await supabase
      .from("branches")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("id", branchId)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching branch:", error);
    return null;
  }
};

/**
 * Fetches branches for a specific organization
 */
export const getOrganizationBranches = async (organizationId: string) => {
  return await supabase
    .from("branches")
    .select("*")
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false });
};

/**
 * Fetches categories for a specific branch
 */
export const getBranchCategories = async (branchId: string) => {
  return await supabase
    .from("categories")
    .select("*")
    .eq("branch_id", branchId)
    .order("sort_order", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: false });
};

/**
 * Fetches menu items for a specific branch with pagination support
 */
export const getBranchMenuItems = async (
  branchId: string,
  page?: number,
  limit?: number
) => {
  let query = supabase
    .from("items")
    .select("*", { count: 'exact' })
    .eq("branch_id", branchId)
    .order("sort_order", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: false });

  // Add pagination if provided
  if (page !== undefined && limit !== undefined) {
    const start = (page - 1) * limit;
    const end = start + limit - 1;
    query = query.range(start, end);
  }

  const { data, error, count } = await query;

  if (error) {
    return {
      data: null,
      error,
      count: 0,
      totalPages: 0
    };
  }

  return {
    data,
    error: null,
    count: count ?? 0,
    totalPages: limit ? Math.ceil((count ?? 0) / limit) : 1
  };
};

/**
 * Creates a new organization
 */
export const createOrganization = async (name: string) => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user)
    return { data: null, error: new Error("User not authenticated") };

  return await supabase
    .from("organizations")
    .insert([
      {
        name,
        owner_id: user.user.id,
        is_setup: false,
      },
    ])
    .select()
    .single();
};

export const updateOrganization = async (
  organizationId: string,
  organizationData: Partial<Organization>
) => {
  return await supabase
    .from("organizations")
    .update({ ...organizationData })
    .eq("id", organizationId)
    .select()
    .single();
};

/**
 * Creates a new branch for an organization
 */
// export const createBranch = async (organizationId: string, branchData: Partial<Branch>) => {
// 	return await supabase
// 		.from('branches')
// 		.insert([
// 			{
// 				id: generateId(), // Add this line to ensure ID is provided
// 				organization_id: organizationId,
// 				created_at: new Date().toISOString(), // Often required by RLS policies
// 				...branchData
// 			}
// 		])
// 		.select()
// 		.single();
// };
export const insertBranch = async (
  dataToSave: Database["public"]["Functions"]["insert_branch"]["Args"]["payload"]
) => {
  try {
    const { data, error } = await supabase.rpc("insert_branch", {
      payload: dataToSave,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};


export const updateBranch = async (
  dataToUpdate: Database["public"]["Functions"]["update_branch"]["Args"]["payload"]
) => {
  try {
    const { data, error } = await supabase.rpc("update_branch", {
      payload: dataToUpdate,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * delete a branch
 */
export const deleteBranch = async (branchId: string) => {
  return await supabase
    .from("branches")
    .delete()
    .eq("id", branchId)
    .select()
    .single();
};

export const insertCategory = async (
  dataToSave: Database["public"]["Functions"]["insert_category"]["Args"]["payload"]
) => {
  try {
    const { data, error } = await supabase.rpc("insert_category", {
      payload: dataToSave,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};


export const updateCategory = async (
  dataToUpdate: Database["public"]["Functions"]["update_category"]["Args"]["payload"]
) => {
  try {
    const { data, error } = await supabase.rpc("update_category", {
      payload: dataToUpdate,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};


export const deleteCategory = async (category_id: string) => {
  try {
    const { data, error } = await supabase.rpc("delete_category", {
      category_id,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getCategories = async (
  branchId: string,
  page?: number,
  limit?: number
) => {
  try {
    let query = supabase
      .from("categories")
      .select("*", { count: 'exact' })
      .eq("branch_id", branchId);

    // Add pagination if provided
    if (page !== undefined && limit !== undefined) {
      const start = (page - 1) * limit;
      const end = start + limit - 1;
      query = query.range(start, end);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      data,
      count: count ?? 0,
      totalPages: limit ? Math.ceil((count ?? 0) / limit) : 1
    };
  } catch (error) {
    console.error(error);
    return {
      data: null,
      count: 0,
      totalPages: 0
    };
  }
};
/**
 * Select a menu item by id
 */
export const selectMenuItemById = async (itemId: string) => {
  return await supabase.from("items").select("*").eq("id", itemId).single();
};


export const insertMenuItem = async (
  dataToSave: Database["public"]["Functions"]["insert_item"]["Args"]["payload"]
) => {
  try {
    const { data, error } = await supabase.rpc("insert_item", {
      payload: dataToSave,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};


export const updateMenuItem = async (
  dataToUpdate: Database["public"]["Functions"]["update_item"]["Args"]["payload"]
) => {
  try {
    // Using update_item for delta-based updates
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("update_item", {
      payload: dataToUpdate,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};


export const deleteMenuItem = async (item_id: string) => {
  try {
    const { data, error } = await supabase.rpc("delete_item", { item_id });
    if (error) throw error;

    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Fetches menu item analytics for a specific item
 */
export const getMenuItemAnalytics = async (itemId: string, branchId: string) => {
  return await supabase
    .from("item_analytics")
    .select("*")
    .eq("item_id", itemId)
    .eq("branch_id", branchId)
    .order("month", { ascending: true })
    .order("year", { ascending: true })
};
interface GetMenuAllItemAnalyticsOptions {
  limit?: number;
  offset?: number;
  itemIds?: string[];
  branchId: string;
}

export const getMenuAllItemAnalytics = async (
  options: GetMenuAllItemAnalyticsOptions = { branchId: "" }
) => {
  const { limit, offset, itemIds, branchId } = options;

  let query = supabase
    .from("item_analytics")
    .select("*", { count: "exact" })
    .eq("branch_id", branchId)
    .order("year", { ascending: true })
    .order("month", { ascending: true });

  if (itemIds && itemIds.length > 0) {
    query = query.in("item_id", itemIds);
  }

  if (typeof limit === "number" && typeof offset === "number") {
    const rangeEnd = offset + limit - 1;
    query = query.range(offset, rangeEnd >= offset ? rangeEnd : offset);
  }

  return await query;
};

// ===== FEATURES CRUD OPERATIONS =====

/**
 * Fetches all available features
 */
export const getBranchFeatures = async (branchId: string) => {
  try {
    const { data, error } = await supabase
      .from("features")
      .select("*")
      .eq("branch_id", branchId)
      .order("category", { ascending: true })
      .order("name", { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error fetching features:", error);
    return { data: null, error };
  }
};

/**
 * Fetches features by category
 */
export const getFeaturesByCategory = async (category: string, branchId: string) => {
  try {
    const { data, error } = await supabase
      .from("features")
      .select("*")
      .eq("category", category)
      .eq("branch_id", branchId)
      .order("name", { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error fetching features by category:", error);
    return { data: null, error };
  }
};


export const AddFeaturesInfo = async (branchId: string, featureId: string, featureData: Feature) => {
  try {
    const { data, error } = await supabase
      .from("features")
      .insert([{ ...featureData, branch_id: branchId, feature_id: featureId }])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error adding branch feature:", error);
    return { data: null, error };
  }
};

export const deleteFeatureInfo = async (featureId: string, branchId: string) => {
  try {
    const { data, error } = await supabase
      .from("features")
      .delete()
      .eq("id", featureId)
      .eq("branch_id", branchId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error deleting feature:", error);
    return { data: null, error };
  }
};

export const updateFeatureInfo = async (featureId: string, branchId: string, featureData: Partial<Feature>) => {
  try {
    const { data, error } = await supabase
      .from("features")
      .update(featureData)
      .eq("id", featureId)
      .eq("branch_id", branchId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error updating feature:", error);
    return { data: null, error };
  }
};

// ===== VARIANT SYSTEM OPERATIONS =====

/**
 * Get all action types
 */
export const getActionTypes = async (): Promise<{ data: ActionType[] | null; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('get_action_types');
    if (error) throw error;
    return { data: (data as any)?.data || null, error: null };
  } catch (error) {
    console.error('Error fetching action types:', error);
    return { data: null, error };
  }
};

/**
 * Get all branch actions by branch ID
 */
export const getAllBranchActionsByBranchId = async (branchId: string): Promise<{ data: BranchActionConfig[] | null; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('get_all_branch_actions_by_branch_id', { p_branch_id: branchId });
    if (error) throw error;

    // Handle different response structures by casting to any
    const rawData = data as any;
    let resultData: BranchActionConfig[] = [];

    if (rawData) {
      // Check if it's wrapped in the expected RPC structure
      if (Array.isArray(rawData) && rawData[0]?.get_all_branch_actions_by_branch_id?.data) {
        resultData = rawData[0].get_all_branch_actions_by_branch_id.data;
      }
      // Or if it's directly the array
      else if (Array.isArray(rawData)) {
        resultData = rawData;
      }
      // Or if it's wrapped in a success response
      else if (rawData.data && Array.isArray(rawData.data)) {
        resultData = rawData.data;
      }
    }

    console.log('Processed branch actions data:', resultData);
    return { data: resultData, error: null };
  } catch (error) {
    console.error('Error fetching branch actions:', error);
    return { data: null, error };
  }
};

/**
 * Get branch action config by ID
 */
export const getBranchActionConfigById = async (id: string): Promise<{ data: BranchActionConfig | null; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('get_branch_action_config_by_id', { p_id: id });
    if (error) throw error;
    return { data: (data as any)?.data || null, error: null };
  } catch (error) {
    console.error('Error fetching branch action config:', error);
    return { data: null, error };
  }
};

/**
 * Make sure an item variant always carries the latest branch action config payload
 */
const hydrateItemVariant = async (variant: ItemVariant | null): Promise<ItemVariant | null> => {
  if (!variant || typeof variant !== 'object') {
    return variant;
  }

  // If data is already present, no hydration needed
  if (variant.data) {
    return variant;
  }

  try {
    const { data: config } = await getBranchActionConfigById(variant.branch_action_config_id);
    if (config) {
      return {
        ...variant,
        data: config.data,
        form_name: variant.form_name || config.form_name,
        action_type: variant.action_type || config.action_type,
      };
    }
  } catch (hydrationError) {
    console.warn('Unable to hydrate item variant with branch action config', hydrationError);
  }

  return variant;
};

/**
 * Insert branch action config
 */
export const insertBranchActionConfig = async (configData: Omit<BranchActionConfig, 'id'>): Promise<{ data: BranchActionConfig | null; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('insert_branch_action_config', { payload: configData });
    if (error) throw error;
    return { data: (data as any)?.data || null, error: null };
  } catch (error) {
    console.error('Error inserting branch action config:', error);
    return { data: null, error };
  }
};

/**
 * Update branch action config
 */
export const updateBranchActionConfig = async (configData: BranchActionConfig): Promise<{ data: BranchActionConfig | null; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('update_branch_action_config', { payload: configData as unknown as Json });
    if (error) throw error;
    return { data: (data as any)?.[0]?.update_branch_action_config?.data || null, error: null };
  } catch (error) {
    console.error('Error updating branch action config:', error);
    return { data: null, error };
  }
};

/**
 * Delete branch action config by ID
 */
export const deleteBranchActionConfigById = async (id: string): Promise<{ data: any; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('delete_branch_action_config_by_id', { p_id: id });
    if (error) throw error;
    return { data: (data as any)?.[0]?.delete_branch_action_config_by_id || null, error: null };
  } catch (error) {
    console.error('Error deleting branch action config:', error);
    return { data: null, error };
  }
};

/**
 * Get all variants by item ID
 */
export const getAllVariantsByItemId = async (itemId: string): Promise<{ data: ItemVariant[] | null; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('get_all_variants_by_item_id', { p_item_id: itemId });
    if (error) throw error;
    return { data: (data as any)?.data || null, error: null };
  } catch (error) {
    console.error('Error fetching item variants:', error);
    return { data: null, error };
  }
};

/**
 * Get item variant by ID
 */
export const getItemVariantById = async (itemId: string): Promise<{ data: ItemVariant | null; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('get_item_variant_by_id', { p_id: itemId });
    if (error) throw error;
    return { data: (data as any)?.[0]?.get_item_variant_by_id?.data || null, error: null };
  } catch (error) {
    console.error('Error fetching item variant:', error);
    return { data: null, error };
  }
};

/**
 * Insert item variant
 */
export const insertItemVariant = async (
  variantData: InsertItemVariantPayload
): Promise<{ data: ItemVariant | null; error: any }> => {
  try {
    const payloadToSend = {
      ...variantData,
      override_data:
        variantData.override_data === undefined ? null : variantData.override_data,
    } as unknown as Json;

    const { data, error } = await supabase.rpc('insert_item_variant', {
      payload: payloadToSend,
    });
    if (error) throw error;

    const raw = data as any;

    if (Array.isArray(raw)) {
      const first = raw[0];
      if (first?.insert_item_variant?.data) {
        const hydrated = await hydrateItemVariant(first.insert_item_variant.data);
        return { data: hydrated, error: null };
      }
      if (first?.data) {
        const hydrated = await hydrateItemVariant(first.data);
        return { data: hydrated, error: null };
      }
      const hydratedFirst = await hydrateItemVariant(first ?? null);
      return { data: hydratedFirst, error: null };
    }

    if (raw?.insert_item_variant?.data) {
      const hydrated = await hydrateItemVariant(raw.insert_item_variant.data);
      return { data: hydrated, error: null };
    }

    if (raw?.data) {
      const hydrated = await hydrateItemVariant(raw.data);
      return { data: hydrated, error: null };
    }

    const hydratedRaw = await hydrateItemVariant(raw || null);
    return { data: hydratedRaw, error: null };
  } catch (error) {
    console.error('Error inserting item variant:', error);
    return { data: null, error };
  }
};

/**
 * Update an existing item variant by its identifier
 */
export const updateItemVariantById = async (
  payload: UpdateItemVariantPayload
): Promise<{ data: ItemVariant | null; error: any }> => {
  try {
    const payloadToSend = {
      ...payload,
      override_data: payload.override_data ?? null,
    } as unknown as Json;

    const { data, error } = await supabase.rpc('update_item_variant_by_id', {
      payload: payloadToSend,
    });
    if (error) throw error;

    const raw = data as any;

    if (Array.isArray(raw)) {
      const first = raw[0];
      if (first?.update_item_variant_by_id?.data) {
        const hydrated = await hydrateItemVariant(first.update_item_variant_by_id.data);
        return { data: hydrated, error: null };
      }
      if (first?.data) {
        const hydrated = await hydrateItemVariant(first.data);
        return { data: hydrated, error: null };
      }
      const hydratedFirst = await hydrateItemVariant(first ?? null);
      return { data: hydratedFirst, error: null };
    }

    if (raw?.update_item_variant_by_id?.data) {
      const hydrated = await hydrateItemVariant(raw.update_item_variant_by_id.data);
      return { data: hydrated, error: null };
    }

    if (raw?.data) {
      const hydrated = await hydrateItemVariant(raw.data);
      return { data: hydrated, error: null };
    }

    const hydratedRaw = await hydrateItemVariant(raw || null);
    return { data: hydratedRaw, error: null };
  } catch (error) {
    console.error('Error updating item variant:', error);
    return { data: null, error };
  }
};

/**
 * Delete item variant by ID
 */
export const deleteItemVariantById = async (id: string): Promise<{ data: any; error: any }> => {
  try {
    const { data, error } = await supabase.rpc('delete_item_variant_by_id', { p_id: id });
    if (error) throw error;
    return { data: (data as any)?.[0]?.delete_item_variant_by_id || null, error: null };
  } catch (error) {
    console.error('Error deleting item variant:', error);
    return { data: null, error };
  }
};


export const getFeedbackSummary = async (payload: FeedbackSummaryPayload): Promise<FeedbackSummaryResponse> => {
  try {
    const { data, error } = await supabase.rpc('get_feedback_summary', { 
      payload: payload as unknown as Json 
    });
    
    if (error) {
      console.error('Error fetching feedback summary:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch feedback summary',
        data: null
      };
    }

    return data as unknown as FeedbackSummaryResponse;
  } catch (error) {
    console.error('Error fetching feedback summary:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      data: null
    };
  }
}

export const getClients = async (payload: GetClientsPayload): Promise<GetClientsResponse> => {
  try {
    const { data, error } = await supabase.rpc('get_clients', { 
      payload: payload as unknown as Json 
    });
    
    if (error) {
      console.error('Error fetching clients:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch clients',
        data: []
      };
    }

    // Handle the response format from the RPC function
    const response = data as any;
    console.log('Raw clients response:', response);
    
    // The response is already in the correct format
    if (response && response.success && Array.isArray(response.data)) {
      return {
        success: response.success,
        message: response.message,
        data: response.data,
        total_count: response.total_count
      };
    }
    
    // Handle wrapped response format
    if (Array.isArray(response) && response[0]?.get_clients) {
      return response[0].get_clients;
    }
    
    return {
      success: true,
      message: 'Clients fetched successfully',
      data: [],
      total_count: 0
    };
  } catch (error) {
    console.error('Error fetching clients:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      data: []
    };
  }
}

export const getItems = async (payload: itemSearchPayload): Promise<{ data: MenuItem[] | null; error: any }> => {
  try {
    // Only include filter fields if they have truthy values
    const cleanPayload: any = {
      branch_id: payload.branch_id,
      category_id: payload.category_id,
      limit: payload.limit,
      offset: payload.offset,
      search: payload.search,
    };

    // Add optional filter fields only if they are defined and not null
    if (payload.is_available !== undefined && payload.is_available !== null) {
      cleanPayload.is_available = payload.is_available;
    }
    if (payload.is_featured !== undefined && payload.is_featured !== null) {
      cleanPayload.is_featured = payload.is_featured;
    }
    if (payload.is_published !== undefined && payload.is_published !== null) {
      cleanPayload.is_published = payload.is_published;
    }

    const { data, error } = await supabase.rpc('get_items', { p_input: cleanPayload as unknown as Json });
    if (error) throw error;
    return { data: (data as any)?.data || data || null, error: null };
  }
  catch (error) {
    console.error('Error fetching items:', error);
    return { data: null, error };
  }
}



// import type { GetBranchOrderSalesArgs, BranchOrderSalesSummary, GetBranchItemSalesArgs, BranchItemSales } from "../types/analytics";

// export const getBranchOrderSales = async (
//   payload: GetBranchOrderSalesArgs
// ): Promise<{ data: BranchOrderSalesSummary | null; error: any }> => {
//   try {
//     const { data, error } = await supabase.rpc("get_branch_order_sales", {
//       p_branch_id: payload.p_branch_id,
//       p_period_type: payload.p_period_type,
//       ...(payload.p_date ? { p_date: payload.p_date } : {}),
//     });
//     if (error) throw error;

//     const unwrappedData = (data as any)?.data || (data as any);
//     // Validate that the data has required fields
//     if (unwrappedData && typeof unwrappedData === 'object' && 'total_orders' in unwrappedData && 'total_sales' in unwrappedData) {
//       return { data: unwrappedData as BranchOrderSalesSummary, error: null };
//     }
//     return { data: null, error: null };
//   } catch (error) {
//     console.error("Error fetching branch order sales:", error);
//     return { data: null, error };
//   }
// };

// export const getBranchItemSales = async (
//   payload: GetBranchItemSalesArgs
// ): Promise<{ data: BranchItemSales[] | null; error: any }> => {
//   try {
//     const { data, error } = await supabase.rpc("get_branch_item_sales", {
//       p_branch_id: payload.p_branch_id,
//       p_period_type: payload.p_period_type,
//       ...(payload.p_date ? { p_date: payload.p_date } : {}),
//     });
//     if (error) throw error;

//     const unwrappedData = (data as any)?.data || (data as any);
//     // Validate that the data is an array with required fields
//     if (Array.isArray(unwrappedData)) {
//       return { data: unwrappedData as BranchItemSales[], error: null };
//     }
//     if (unwrappedData && Array.isArray(unwrappedData)) {
//       return { data: unwrappedData as BranchItemSales[], error: null };
//     }
//     return { data: null, error: null };
//   } catch (error) {
//     console.error("Error fetching branch item sales:", error);
//     return { data: null, error };
//   }
// };

/**
 * Batch-update sort_order for multiple categories
 */
export const batchUpdateCategorySortOrders = async (
  updates: { id: string; sort_order: number }[]
): Promise<{ error: any }> => {
  try {
    await Promise.all(
      updates.map(({ id, sort_order }) =>
        supabase.from("categories").update({ sort_order }).eq("id", id)
      )
    );
    return { error: null };
  } catch (error) {
    return { error };
  }
};

/**
 * Batch-update sort_order for multiple items
 */
export const batchUpdateItemSortOrders = async (
  updates: { id: string; sort_order: number }[]
): Promise<{ error: any }> => {
  try {
    await Promise.all(
      updates.map(({ id, sort_order }) =>
        supabase.from("items").update({ sort_order }).eq("id", id)
      )
    );
    return { error: null };
  } catch (error) {
    return { error };
  }
};
